import fs from "node:fs";
import path from "node:path";
import readline from "node:readline";
import { spawn } from "node:child_process";
import { api, listMyOrgs } from "../lib/api.js";
import { createArchive } from "../lib/archive.js";
import {
  findWorkspaceRoot,
  manifestKind,
  readManifest,
  readProject,
  readWorkspaceConfig,
  saveProject,
  type ProjectConfig,
} from "../lib/config.js";
import { pollUntilReady } from "../lib/poll.js";
import { fetchSchema, writeSchema } from "../lib/sync-schema.js";

interface DeploySuccess {
  app_id: string;
  slug: string;
  app_token: string;
}

interface SlugConflict {
  error: "slug_exists_in_org";
  existing_app_id: string;
  existing_app_name: string;
  slug: string;
  suggested_slug: string;
}

export interface DeployOptions {
  from?: string;
  path?: string;
  name?: string;
  org?: string;
  yes?: boolean;
  updateExisting?: boolean;
  createNew?: boolean;
  dryRun?: boolean;
  noSync?: boolean;
}

// Exit codes: keep separate ones so CI/agents can branch on outcome.
const EXIT_USAGE = 64;
const EXIT_CONFLICT_NEEDS_DECISION = 75;

function ask(question: string): Promise<string> {
  const rl = readline.createInterface({
    input: process.stdin,
    output: process.stdout,
  });
  return new Promise((resolve) => {
    rl.question(question, (answer) => {
      rl.close();
      resolve(answer.trim());
    });
  });
}

async function uploadDeploy(
  archive: Buffer,
  name: string,
  framework: string,
  orgId?: string,
): Promise<{ ok: true; data: DeploySuccess } | { ok: false; conflict: SlugConflict } | { ok: false; error: string }> {
  const formData = new FormData();
  formData.append("source", new Blob([archive]), "source.tar.gz");
  formData.append("name", name);
  formData.append("framework", framework);

  const res = await api.postForm("/v1/apps/deploy", formData, { orgId });
  if (res.ok) {
    const data = (await res.json()) as DeploySuccess;
    return { ok: true, data };
  }
  if (res.status === 409) {
    const conflict = (await res.json()) as SlugConflict;
    return { ok: false, conflict };
  }
  const err = (await res.json().catch(() => ({}))) as { error?: string };
  return { ok: false, error: err.error || res.statusText };
}

async function uploadRedeploy(
  appId: string,
  archive: Buffer,
  orgId?: string,
): Promise<{ ok: true; data: DeploySuccess } | { ok: false; error: string }> {
  const formData = new FormData();
  formData.append("source", new Blob([archive]), "source.tar.gz");

  const res = await api.postForm(`/v1/apps/${appId}/redeploy`, formData, { orgId });
  if (res.ok) {
    const data = (await res.json()) as DeploySuccess;
    return { ok: true, data };
  }
  const err = (await res.json().catch(() => ({}))) as { error?: string };
  return { ok: false, error: err.error || res.statusText };
}

function assertNodeVersion() {
  const [major] = process.versions.node.split(".").map(Number);
  if (major < 22) {
    console.error(
      `\n  Clawnify requires Node.js v22 or later.\n` +
      `  You are using v${process.versions.node}.\n\n` +
      `  Install a newer version: https://nodejs.org\n` +
      `  Or use a version manager: nvm use 22\n`
    );
    process.exit(1);
  }
}

async function resolveOrgId(
  options: DeployOptions,
  linkedProject: ProjectConfig | null,
  resolvedDir?: string,
): Promise<string | undefined> {
  if (options.org) {
    const orgs = await listMyOrgs();
    const match = orgs.find((o) => o.id === options.org || o.slug === options.org);
    if (!match) {
      console.error(
        `No org matched "${options.org}". Available: ${orgs.map((o) => o.slug).join(", ")}`,
      );
      process.exit(EXIT_USAGE);
    }
    if (linkedProject?.app_id && linkedProject.org_id && linkedProject.org_id !== match.id) {
      console.error(
        `This project is linked to a different org (${linkedProject.org_id}).\n` +
        `  --org ${options.org} resolves to ${match.id}.\n` +
        `  Re-link first: clawnify link ${linkedProject.app_id} --org ${options.org}`,
      );
      process.exit(1);
    }
    return match.id;
  }
  // Only honor project.json's org_id when the project is actually linked.
  // An unlinked project (no app_id) should defer to the user's active org
  // so `clawnify org use X && clawnify deploy` works in a fresh directory.
  if (linkedProject?.app_id && linkedProject.org_id) {
    return linkedProject.org_id;
  }
  // A fresh (unlinked) target inside a workspace deploys to the workspace's
  // org, not whatever global active org happens to be set. Once linked (above),
  // that link wins and this is skipped.
  if (resolvedDir) {
    const wsOrg = await workspaceOrgId(resolvedDir);
    if (wsOrg) return wsOrg;
  }
  return undefined;
}

/**
 * Resolve the org a workspace is bound to (slug or id in its clawnify.json),
 * or undefined if `resolvedDir` is not inside a workspace. Exits with a clear
 * error if the workspace names an org the user can't access.
 */
async function workspaceOrgId(resolvedDir: string): Promise<string | undefined> {
  const ws = findWorkspaceRoot(resolvedDir);
  if (!ws?.org) return undefined;
  const orgs = await listMyOrgs();
  const match = orgs.find((o) => o.id === ws.org || o.slug === ws.org);
  if (!match) {
    console.error(
      `This workspace is bound to org "${ws.org}", but you're not a member of it ` +
      `(or it no longer exists).\n  Available: ${orgs.map((o) => o.slug).join(", ")}`,
    );
    process.exit(EXIT_USAGE);
  }
  return match.id;
}

type ConflictDecision = "update" | "create" | "cancel";

async function resolveConflictDecision(
  options: DeployOptions,
  conflict: SlugConflict,
): Promise<ConflictDecision> {
  if (options.updateExisting) return "update";
  if (options.createNew) return "create";
  if (options.yes) return "update";

  if (!process.stdin.isTTY) {
    console.error(
      `\n  Slug '${conflict.slug}' is taken in this organization.\n` +
      `  Pass one of:\n` +
      `    --update-existing       overwrite the existing app\n` +
      `    --create-new            deploy as '${conflict.suggested_slug}'\n` +
      `    --yes                   default to update (safest)\n`,
    );
    process.exit(EXIT_CONFLICT_NEEDS_DECISION);
  }

  console.log(
    `\n  Slug '${conflict.slug}' is taken in this organization` +
    ` (currently held by an app named '${conflict.existing_app_name}').\n`,
  );
  console.log(`    1) Update that app — deploy this source over it`);
  console.log(`    2) Create a new app at slug '${conflict.suggested_slug}'\n`);
  const answer = await ask("  Choose [1/2] (default 1): ");

  if (answer === "" || answer === "1") return "update";
  if (answer === "2") return "create";

  console.error(
    `\n  Cancelled. Received '${answer}' — expected 1 or 2.\n` +
    `  For non-interactive use, pass --update-existing or --create-new.\n`,
  );
  return "cancel";
}

function summarizeArchive(buf: Buffer, files: string[], framework: string, name: string) {
  const sizeKB = (buf.length / 1024).toFixed(1);
  console.log(`\n  Dry run — would deploy '${name}' (${framework})`);
  console.log(`  Archive: ${files.length} files, ${sizeKB}KB gzipped\n`);
  const preview = files.slice(0, 25);
  for (const f of preview) console.log(`    ${f}`);
  if (files.length > preview.length) {
    console.log(`    ... and ${files.length - preview.length} more`);
  }
  console.log();
}

interface WebsiteDeployResponse {
  website_id: string;
  slug: string;
  status: string;
  url: string;
}

async function uploadWebsiteDeploy(
  archive: Buffer,
  name: string | undefined,
  orgId?: string,
): Promise<
  | { ok: true; data: WebsiteDeployResponse }
  | { ok: false; error: string }
> {
  const formData = new FormData();
  formData.append("source", new Blob([archive]), "source.tar.gz");
  if (name) formData.append("name", name);

  const res = await api.postForm("/v1/websites/deploy", formData, { orgId });
  if (res.ok) {
    const data = (await res.json()) as WebsiteDeployResponse;
    return { ok: true, data };
  }
  const err = (await res.json().catch(() => ({}))) as { error?: string };
  return { ok: false, error: err.error || res.statusText };
}

async function deployWebsite(dir: string | undefined, options: DeployOptions): Promise<void> {
  if (options.from) {
    console.error("--from is app-only for now. Run `clawnify deploy` inside the website project directory.");
    process.exit(EXIT_USAGE);
  }

  const resolvedDir = dir ? path.resolve(dir) : process.cwd();
  const manifest = readManifest(resolvedDir);
  const baseName = options.name || manifest?.name || path.basename(resolvedDir);
  const linkedProject = readProject(resolvedDir);
  const { archive, files } = createArchive(resolvedDir);

  // Org resolution mirrors the app path. A linked website's org_id wins
  // unless the user passed --org explicitly.
  let orgId: string | undefined;
  if (options.org) {
    const orgs = await listMyOrgs();
    const match = orgs.find((o) => o.id === options.org || o.slug === options.org);
    if (!match) {
      console.error(
        `No org matched "${options.org}". Available: ${orgs.map((o) => o.slug).join(", ")}`,
      );
      process.exit(EXIT_USAGE);
    }
    if (linkedProject?.website_id && linkedProject.org_id && linkedProject.org_id !== match.id) {
      console.error(
        `This project is linked to a different org (${linkedProject.org_id}).\n` +
        `  --org ${options.org} resolves to ${match.id}.`,
      );
      process.exit(1);
    }
    orgId = match.id;
  } else if (linkedProject?.website_id && linkedProject.org_id) {
    orgId = linkedProject.org_id;
  } else {
    // Fresh website inside a workspace → deploy to the workspace's org.
    orgId = await workspaceOrgId(resolvedDir);
  }

  if (options.dryRun) {
    const sizeKB = (archive.length / 1024).toFixed(1);
    console.log(`\n  Dry run — would deploy website '${baseName}' (astro)`);
    console.log(`  Archive: ${files.length} files, ${sizeKB}KB gzipped\n`);
    const preview = files.slice(0, 25);
    for (const f of preview) console.log(`    ${f}`);
    if (files.length > preview.length) {
      console.log(`    ... and ${files.length - preview.length} more`);
    }
    console.log();
    return;
  }

  console.log(linkedProject?.website_id ? `Updating ${baseName}...` : `Deploying ${baseName}...`);
  const result = await uploadWebsiteDeploy(archive, baseName, orgId);
  if (!result.ok) {
    console.error(`Deploy failed: ${result.error}`);
    process.exit(1);
  }

  saveProject(resolvedDir, {
    kind: "website",
    org_id: orgId,
    website_id: result.data.website_id,
    slug: result.data.slug,
  });

  const polled = await pollUntilReady(result.data.website_id, {
    kind: "website",
    orgId,
  });
  if (polled.status === "error") {
    console.error(`\n  Deploy failed: ${polled.error || "Unknown error"}\n`);
    process.exit(1);
  }

  // Draft-first: the deploy lands on the draft tier (status 'preview').
  // Publishing to the live URL is an explicit action from the dashboard.
  if (polled.status === "live") {
    console.log(`\n  ✓ Published!  ${result.data.url}\n`);
  } else {
    console.log(
      `\n  ✓ Deployed to draft.\n` +
      `  Review it in the dashboard, then "Publish to live" when ready.\n`,
    );
  }
}

export async function deployCommand(
  dir?: string,
  options: DeployOptions = {},
): Promise<void> {
  assertNodeVersion();

  // Dispatch on manifest kind early — websites take a completely different
  // path (no slug conflicts, no schema sync, different API endpoint).
  if (!options.from) {
    const resolvedDir = dir ? path.resolve(dir) : process.cwd();
    const manifest = readManifest(resolvedDir);
    // A workspace root is a monorepo of apps, not a deployable app itself.
    // Refuse to tar the whole tree; point the user at a specific app.
    if (!manifest && readWorkspaceConfig(resolvedDir)) {
      console.error(
        "This is a Clawnify workspace root, not an app.\n" +
        "  Deploy a specific app:  clawnify deploy apps/<name>\n" +
        "  Or cd into one:         cd apps/<name> && clawnify deploy",
      );
      process.exit(EXIT_USAGE);
    }
    if (manifest) {
      try {
        const kind = manifestKind(manifest);
        if (kind === "website") {
          return deployWebsite(dir, options);
        }
      } catch (err) {
        console.error(`${(err as Error).message}`);
        process.exit(EXIT_USAGE);
      }
    }
    // No manifest → fall through to the app path (it tolerates a missing
    // manifest by defaulting framework to vite-preact).
  }

  let appId: string;
  let slug: string;
  let appToken: string;
  let resolvedOrgId: string | undefined;
  let syncTargetDir: string | undefined;

  if (options.from) {
    const repo = options.from.replace(/^github:/, "");
    const subdir = options.path?.trim().replace(/^\/+|\/+$/g, "") || undefined;
    if (options.dryRun) {
      console.log(
        `\n  Dry run — would deploy from GitHub repo '${repo}' (branch: main${subdir ? `, path: ${subdir}` : ""})\n`,
      );
      return;
    }
    console.log(`Deploying from ${repo}${subdir ? `/${subdir}` : ""}...`);

    const orgId = options.org ? (await resolveOrgId(options, null)) : undefined;
    const res = await api.post("/v1/apps/deploy", { repo, branch: "main", subdir }, { orgId });
    if (!res.ok) {
      const err = (await res.json().catch(() => ({}))) as { error?: string };
      console.error(`Deploy failed: ${err.error || res.statusText}`);
      process.exit(1);
    }

    const data = (await res.json()) as DeploySuccess;
    appId = data.app_id;
    slug = data.slug;
    appToken = data.app_token;
    resolvedOrgId = orgId;
    // syncTargetDir intentionally left undefined: deploying from a remote
    // repo has no canonical local directory to mirror back into.
  } else {
    const resolvedDir = dir ? path.resolve(dir) : process.cwd();
    syncTargetDir = resolvedDir;
    const manifest = readManifest(resolvedDir);
    const baseName = options.name || manifest?.name || path.basename(resolvedDir);
    const framework = manifest?.app?.framework || "vite-preact";
    const linkedProject = readProject(resolvedDir);
    await regenerateDrizzleMigrations(resolvedDir);
    const { archive, files } = createArchive(resolvedDir);
    const orgId = await resolveOrgId(options, linkedProject, resolvedDir);

    if (options.dryRun) {
      summarizeArchive(archive, files, framework, baseName);
      if (linkedProject?.app_id) {
        console.log(`  Would update existing app: ${linkedProject.slug ?? linkedProject.app_id}\n`);
      } else {
        console.log(`  Would create new app: ${baseName}\n`);
      }
      return;
    }

    if (linkedProject?.app_id) {
      console.log(`Updating ${baseName} (${linkedProject.slug ?? linkedProject.app_id})...`);
      const result = await uploadRedeploy(linkedProject.app_id, archive, orgId);
      if (!result.ok) {
        console.error(`Deploy failed: ${result.error}`);
        process.exit(1);
      }
      appId = result.data.app_id;
      slug = result.data.slug;
      appToken = result.data.app_token;
      saveProject(resolvedDir, { org_id: orgId, app_id: appId, slug, app_token: appToken });
    } else {
      console.log(`Deploying ${baseName}...`);
      let result = await uploadDeploy(archive, baseName, framework, orgId);

      if (!result.ok && "conflict" in result) {
        const decision = await resolveConflictDecision(options, result.conflict);

        if (decision === "update") {
          const redeployResult = await uploadRedeploy(result.conflict.existing_app_id, archive, orgId);
          if (!redeployResult.ok) {
            console.error(`Deploy failed: ${redeployResult.error}`);
            process.exit(1);
          }
          appId = redeployResult.data.app_id;
          slug = redeployResult.data.slug;
          appToken = redeployResult.data.app_token;
        } else if (decision === "create") {
          result = await uploadDeploy(archive, result.conflict.suggested_slug, framework, orgId);
          if (!result.ok) {
            const errMsg = "conflict" in result ? "Slug still conflicts" : result.error;
            console.error(`Deploy failed: ${errMsg}`);
            process.exit(1);
          }
          appId = result.data.app_id;
          slug = result.data.slug;
          appToken = result.data.app_token;
        } else {
          process.exit(EXIT_CONFLICT_NEEDS_DECISION);
        }
      } else if (!result.ok) {
        console.error(`Deploy failed: ${result.error}`);
        process.exit(1);
      } else {
        appId = result.data.app_id;
        slug = result.data.slug;
        appToken = result.data.app_token;
      }

      saveProject(resolvedDir, { org_id: orgId, app_id: appId, slug, app_token: appToken });
      resolvedOrgId = orgId;
    }
  }

  const result = await pollUntilReady(appId);

  if (result.status === "error") {
    console.error(`\n  Deploy failed: ${result.error || "Unknown error"}\n`);
    process.exit(1);
  }

  const url = `https://${slug}.apps.clawnify.com`;
  console.log(`\n  ✓ Deployed!  ${url}\n`);
  console.log(`  Open:  clawnify open ${slug}\n`);

  // Auto-sync schema.sql from the deployed app back into the local repo
  // so the local copy mirrors the deployed schema after migrations apply.
  // Skipped when deploying from a remote --from <repo> (no local dir to
  // sync into) or when --no-sync is passed. Failures here don't fail the
  // deploy — the deploy itself already succeeded.
  if (syncTargetDir && !options.noSync && resolvedOrgId) {
    try {
      const schema = await fetchSchema({ appId, orgId: resolvedOrgId });
      const target = path.join(syncTargetDir, "schema.sql");
      const writeResult = await writeSchema(target, schema);
      const rel = path.relative(process.cwd(), writeResult.path) || writeResult.path;
      switch (writeResult.status) {
        case "written":
          console.log(`  Synced ${rel} (${writeResult.bytes} bytes)\n`);
          break;
        case "refused-uncommitted":
          console.log(
            `  ${rel} has uncommitted changes — not overwriting.\n` +
              `  Run \`clawnify pull schema --force --from ${slug}\` to overwrite.\n`,
          );
          break;
        case "refused-different":
          console.log(
            `  ${rel} differs from the deployed copy (not in git).\n` +
              `  Run \`clawnify pull schema --force --from ${slug}\` to overwrite.\n`,
          );
          break;
        case "no-schema":
        case "unchanged":
          // Silent — nothing to report.
          break;
      }
    } catch (err) {
      console.log(`  (schema sync skipped: ${(err as Error).message})\n`);
    }
  }
}

/**
 * Run `drizzle-kit generate` so .clawnify/drizzle/*.sql is current
 * with src/server/schema.ts. archive.ts auto-includes those files
 * remapped to `drizzle/<file>` in the uploaded tar; the deploy
 * pipeline (PR #444) reads them from R2 and applies via the
 * existing applyPendingMigrations path.
 *
 * No-op for apps that don't use the drizzle path (legacy schema.sql,
 * no schema.ts, or no drizzle.config.ts). Failure is non-fatal:
 * we warn and continue, so a deploy never silently breaks just
 * because a user hasn't set up drizzle-kit yet.
 */
async function regenerateDrizzleMigrations(resolvedDir: string): Promise<void> {
  const schemaTs = path.join(resolvedDir, "src/server/schema.ts");
  const drizzleConfig = path.join(resolvedDir, "drizzle.config.ts");
  if (!fs.existsSync(schemaTs) || !fs.existsSync(drizzleConfig)) return;

  await new Promise<void>((resolve) => {
    const proc = spawn("npx", ["drizzle-kit", "generate"], {
      cwd: resolvedDir,
      stdio: ["ignore", "pipe", "pipe"],
      env: { ...process.env },
    });
    let stderr = "";
    proc.stderr?.on("data", (d: Buffer) => { stderr += d.toString(); });
    proc.on("exit", (code) => {
      if (code !== 0) {
        console.warn(
          `  warning: drizzle-kit generate failed (${stderr.trim().slice(0, 200)}). ` +
            `Continuing with existing .clawnify/drizzle/ contents.`,
        );
      }
      resolve();
    });
    proc.on("error", () => resolve());
  });
}
